/**
 * Example DSL files for the playground
 */

export interface Example {
  name: string;
  description: string;
  code: string;
}

export const examples: Example[] = [
  {
    name: "Hello World",
    description: "Simple text display",
    code: `screen Hello {
  Column(padding: 16, spacing: 8) {
    Text("Hello, Pyramid!", size: 24, weight: bold)
    Text("Your first server-driven UI", color: #666666)
  }
}`,
  },
  {
    name: "Product Card",
    description: "E-commerce product display",
    code: `screen ProductDetail("/product/:id") {
  data {
    product: Product
    quantity: Int = 1
  }

  Column(padding: 16, spacing: 16) {
    Image(product.imageUrl, height: 300, cornerRadius: 12)
    
    Text(product.name, size: 24, weight: bold)
    
    Row(spacing: 8) {
      Text("$\${product.price}", size: 20, color: #1a73e8)
      
      if product.onSale {
        Badge("\${product.discount}% OFF", color: #e53935)
      }
    }
    
    Row(spacing: 12) {
      Stepper(
        quantity,
        min: 1,
        max: 10,
        onChange: set_state("quantity", value)
      )
      
      Button(
        "Add to Cart",
        fillWidth: true,
        onTap: api(POST, "/cart/add", { productId: product.id, quantity: quantity })
      )
    }
  }
}

type Product {
  id: String
  name: String
  price: Float
  imageUrl: String
  onSale: Bool?
  discount: Int?
}`,
  },
  {
    name: "User Profile",
    description: "Profile page with conditionals",
    code: `screen UserProfile("/profile/:userId") {
  data {
    user: User
    isOwnProfile: Bool = false
  }

  Column(padding: 16, spacing: 16) {
    // Header with avatar
    Row(spacing: 16, alignment: center) {
      Avatar(user.avatarUrl, size: 80)
      
      Column(spacing: 4) {
        Text(user.name, size: 20, weight: bold)
        Text("@\${user.username}", color: #666666)
        
        if user.verified {
          Badge("Verified", color: #4caf50)
        }
      }
    }
    
    Divider()
    
    // Stats
    Row(spacing: 24, arrangement: spaceEvenly) {
      Column(alignment: center) {
        Text("\${user.followers}", size: 18, weight: bold)
        Text("Followers", color: #666666, size: 12)
      }
      
      Column(alignment: center) {
        Text("\${user.following}", size: 18, weight: bold)
        Text("Following", color: #666666, size: 12)
      }
      
      Column(alignment: center) {
        Text("\${user.posts}", size: 18, weight: bold)
        Text("Posts", color: #666666, size: 12)
      }
    }
    
    // Action buttons
    if isOwnProfile {
      Button("Edit Profile", variant: outlined, fillWidth: true)
    } else {
      Row(spacing: 12) {
        Button("Follow", fillWidth: true)
        Button("Message", variant: outlined, fillWidth: true)
      }
    }
  }
}

type User {
  id: String
  name: String
  username: String
  avatarUrl: String
  verified: Bool
  followers: Int
  following: Int
  posts: Int
}`,
  },
  {
    name: "Item List",
    description: "ForEach loop with items",
    code: `screen TodoList {
  data {
    todos: Todo[]
    filter: String = "all"
  }

  Column(padding: 16, spacing: 12) {
    // Header
    Row(spacing: 8, arrangement: spaceBetween) {
      Text("My Tasks", size: 24, weight: bold)
      Badge("\${todos.length} items")
    }
    
    // Filter chips
    Row(spacing: 8) {
      Chip("All", selected: filter == "all", onTap: set_state("filter", "all"))
      Chip("Active", selected: filter == "active", onTap: set_state("filter", "active"))
      Chip("Done", selected: filter == "done", onTap: set_state("filter", "done"))
    }
    
    Divider()
    
    // Todo items
    ForEach(todos) { todo ->
      Card(padding: 12) {
        Row(spacing: 12, alignment: center) {
          Checkbox(
            todo.completed,
            onToggle: api(PATCH, "/todos/\${todo.id}", { completed: !todo.completed })
          )
          
          Column(spacing: 4) {
            Text(todo.title, weight: medium)
            
            if todo.dueDate {
              Text("Due: \${todo.dueDate}", size: 12, color: #666666)
            }
          }
          
          Spacer()
          
          IconButton(
            icon: "delete",
            onTap: api(DELETE, "/todos/\${todo.id}")
          )
        }
      }
    }
    
    // Add button
    FAB(
      icon: "add",
      onTap: navigate("/todos/new")
    )
  }
}

type Todo {
  id: String
  title: String
  completed: Bool
  dueDate: String?
}`,
  },
  {
    name: "Settings Page",
    description: "Forms and switches",
    code: `screen Settings {
  data {
    settings: UserSettings
  }

  Column(padding: 16, spacing: 0) {
    Text("Settings", size: 24, weight: bold)
    
    Spacer(height: 16)
    
    // Notifications section
    Text("Notifications", size: 14, weight: semibold, color: #666666)
    
    ListTile(
      title: "Push Notifications",
      subtitle: "Receive push notifications",
      trailing: Switch(settings.pushEnabled, onToggle: api(PATCH, "/settings", { pushEnabled: !settings.pushEnabled }))
    )
    
    ListTile(
      title: "Email Notifications",
      subtitle: "Receive email updates",
      trailing: Switch(settings.emailEnabled, onToggle: api(PATCH, "/settings", { emailEnabled: !settings.emailEnabled }))
    )
    
    Divider()
    
    // Appearance section
    Text("Appearance", size: 14, weight: semibold, color: #666666)
    
    ListTile(
      title: "Dark Mode",
      subtitle: "Use dark theme",
      trailing: Switch(settings.darkMode, onToggle: api(PATCH, "/settings", { darkMode: !settings.darkMode }))
    )
    
    ListTile(
      title: "Language",
      subtitle: settings.language,
      onTap: navigate("/settings/language")
    )
    
    Divider()
    
    // Account section
    Text("Account", size: 14, weight: semibold, color: #666666)
    
    ListTile(
      title: "Edit Profile",
      onTap: navigate("/profile/edit")
    )
    
    ListTile(
      title: "Change Password",
      onTap: navigate("/settings/password")
    )
    
    ListTile(
      title: "Sign Out",
      titleColor: #e53935,
      onTap: api(POST, "/auth/logout")
    )
  }
}

type UserSettings {
  pushEnabled: Bool
  emailEnabled: Bool
  darkMode: Bool
  language: String
}`,
  },
];

export const defaultExample = examples[0];
